/**
 * Calendar dates: a day such as "2026-11-03", with no time and no timezone.
 *
 * Rotations are facts about business days. Using JS `Date` for them invites a UTC offset to
 * shift a placement onto the wrong day, so this module owns every date decision once:
 * - month arithmetic clamps to the end of the month (31 Jan + 1 month = 28/29 Feb),
 * - "today" is always computed in the business's timezone (Africa/Johannesburg by default),
 * - ISO strings compare correctly with < and >.
 */

export type CalendarDate = string & { readonly __brand: "CalendarDate" };

const PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_PER_DAY = 86_400_000;

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function format(year: number, month: number, day: number): CalendarDate {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}` as CalendarDate;
}

function parts(date: CalendarDate): [number, number, number] {
  return date.split("-").map(Number) as [number, number, number];
}

/** @throws on anything that isn't a real calendar day in YYYY-MM-DD form. */
export function calendarDate(raw: string): CalendarDate {
  const match = PATTERN.exec(raw);
  if (match) {
    const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
    if (month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(year, month)) {
      return raw as CalendarDate;
    }
  }
  throw new Error(`Invalid date "${raw}": expected a real day as YYYY-MM-DD`);
}

export function isCalendarDate(raw: unknown): raw is CalendarDate {
  if (typeof raw !== "string") return false;
  try {
    calendarDate(raw);
    return true;
  } catch {
    return false;
  }
}

/** Whole calendar months, clamped to the target month's last day. */
export function addMonths(date: CalendarDate, months: number): CalendarDate {
  if (!Number.isInteger(months)) throw new Error(`addMonths needs a whole number, got ${months}`);
  const [y, m, d] = parts(date);
  const index = y * 12 + (m - 1) + months;
  const year = Math.floor(index / 12);
  const month = (index % 12) + 1;
  return format(year, month, Math.min(d, daysInMonth(year, month)));
}

export function addDays(date: CalendarDate, days: number): CalendarDate {
  if (!Number.isInteger(days)) throw new Error(`addDays needs a whole number, got ${days}`);
  const [y, m, d] = parts(date);
  const shifted = new Date(Date.UTC(y, m - 1, d) + days * MS_PER_DAY);
  return format(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate());
}

/** The calendar day an instant falls on in `timeZone`. The caller supplies the instant (no clock reads here). */
export function calendarDateAt(instant: Date, timeZone = "Africa/Johannesburg"): CalendarDate {
  // en-CA formats as YYYY-MM-DD.
  const formatted = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(instant);
  return calendarDate(formatted);
}

/** Whole days from `a` to `b` (positive when b is later). */
export function daysBetween(a: CalendarDate, b: CalendarDate): number {
  const utc = (d: CalendarDate) => {
    const [y, m, day] = parts(d);
    return Date.UTC(y, m - 1, day);
  };
  return Math.round((utc(b) - utc(a)) / MS_PER_DAY);
}
